import React, {useEffect, useState} from 'react';
import {
    FormGroup, Grid,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Switch
} from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import {formatDateUS} from "../../../helpers/utils";
import {makeStyles} from "@material-ui/styles";
import {ChangeEventChip} from "./Chips";
import Typography from "@material-ui/core/Typography";
import _ from "lodash";

const useStyles = makeStyles((theme) => ({
    table: {
        width: '100%'
    },
    changesDialog: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        outline: 'none',
        boxShadow: theme.shadows[20],
        width: '76%',
        height: '76%',
        maxHeight: '100%',
        overflowY: 'auto',
        maxWidth: '100%',
        background: '#F1F3F4'
    },
    tableHeadCell: {
        fontWeight: 700
    },
    tableBodyCell: {
        fontWeight: 300
    },
    toolbar: {
        background: '#fafafa',
        borderBottom: '1px solid #c3c3c3'
    },
    toolbarTitle: {
        flex: '1 1',
        fontWeight: 700
    }
}));

const capitalize = (s) => {
    if (typeof s !== 'string') return ''
    return s.toLowerCase().replace(/(?:^|\s|-)\S/g, x => x.toUpperCase())
}

const toNumericIfPossible = (v) => {
    if (v === null || v === undefined || v === '') return v;
    if (typeof v === 'number') return v;
    if (typeof v !== 'string') return v;
    const trimmed = v.trim();
    if (trimmed === '') return v;
    const n = Number(trimmed);
    if (!isNaN(n)) return n;
    return v;
}

const valuesAreEquivalent = (a, b) => {
    // Keep null vs "" as a real change
    if ((a === null || a === undefined) && b === '') return false;
    if ((b === null || b === undefined) && a === '') return false;
    // Treat numerically equal values (e.g. 12 vs "12", 12 vs 12.0) as the same
    return toNumericIfPossible(a) === toNumericIfPossible(b);
}

const isDiffEmpty = (html) => {
    if (!html) return true;
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent.trim() === '';
}

const isEmptyChange = (change) => {
    return isDiffEmpty(change?.left_diff) && isDiffEmpty(change?.right_diff);
}
const getFieldsWithChanges = (version) => {
    const realChanges = [];
    const keys = Object.keys(version.changes);
    for(const key of keys) {
        const hasChangesetEntry = !!version?.changeset && Object.prototype.hasOwnProperty.call(version.changeset, key);
        const [left, right] = hasChangesetEntry ? version.changeset[key] : [];
        if (hasChangesetEntry && !valuesAreEquivalent(left, right) && !isEmptyChange(version.changes[key])) {
            realChanges.push(key);
        }
    }
    return realChanges;
}

const hasChanges = (version) => {
    return getFieldsWithChanges(version).length > 0;
}

const nestedModels = [
    "CompanyContact",
    "CompanyContactDraft",
    "ContactUniversity",
    "CurrentContactFoundationWork",
    "RiaContactUniversity",
    "FoundationContactCompany",
    "RelatedFirm",
];

const ChangeLogModal = ({open, onClose, changes, changedBy, automatic, userIsPM, ...props}) => {
    const no_admin_change = changedBy ?? 'FintrX Importer';
    const [showAll, setShowAll] = useState(true);
    const hasMultipleTypes = _.uniqBy(changes, 'item_type').length > 1;
    const [realChanges, setRealChanges] = useState([])
    const classes = useStyles();

    useEffect(() => {
        const filtered = changes?.filter(c => {
            if (hasChanges(c)) {
                return (showAll || c.who_did_it);
            } else {
                return false;
            }
        })

        setRealChanges(filtered)
    },[showAll, changes])


    const displayType = (version) => {
        if (nestedModels.includes(version.item_type)) {
            return version.item_name ? `${version.item_type} - ${version.item_name.trim()}` : version.item_type
        } else {
            return version.item_type
        }
    }

    const renderChanges = (version) => {
        const keys = getFieldsWithChanges(version);

        return keys.map(key => (
            <TableRow>
                <TableCell align="left">
                    {capitalize(key.replaceAll('_', ' '))}
                    { hasMultipleTypes && (<strong> ({ displayType(version) }) </strong>)}
                </TableCell>
                <TableCell align="center" style={{maxWidth: '20vw', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal'}} dangerouslySetInnerHTML={{__html: version.changes[key].left_diff} }/>
                <TableCell align="center" style={{maxWidth: '20vw', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal'}} dangerouslySetInnerHTML={{__html: version.changes[key].right_diff}}/>
            </TableRow>
        ));
    }

    const onShowAllClicked = () => {
        setShowAll(!showAll)
    }



    return (
        <Modal onClose={onClose}
               open={open}>
            <Paper className={classes.changesDialog}>
                <Toolbar className={classes.toolbar}>
                    <h3 className={classes.toolbarTitle}>Change Log</h3>
                    { automatic && <FormGroup>
                            <Typography component="div">
                                <Grid component="label" container alignItems="center" spacing={1}>
                                    <Grid item>Manual</Grid>
                                    <Grid item>
                                        <Switch checked={showAll}
                                                onChange={onShowAllClicked} />
                                    </Grid>
                                    <Grid item>All Changes</Grid>
                                </Grid>
                            </Typography>
                        </FormGroup>
                    }
                </Toolbar>
                <TableContainer>
                    <Table className={classes.table} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.tableHeadCell}  align="center" style={{width: '80px'}}>Event</TableCell>
                                <TableCell className={classes.tableHeadCell}  align="center" style={{width: '80px'}}>Date</TableCell>
                                <TableCell className={classes.tableHeadCell}  align="center" style={{width: '140px'}}>Who Did It</TableCell>
                                <TableCell className={classes.tableHeadCell}  align="center">Changes</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {realChanges?.map((row) => (
                                <TableRow key={row.modified_at}>
                                    <TableCell className={classes.tableBodyCell}  align="center" component="th" scope="row">
                                        <ChangeEventChip event={(row.pm_event && userIsPM && row.event === 'update') ? 'pm_update' : row.event}/>
                                    </TableCell>
                                    <TableCell className={classes.tableBodyCell}  align="center">{formatDateUS(row.modified_at)}</TableCell>
                                    <TableCell className={classes.tableBodyCell}  align="center">{(row.who_did_it || row.whodunnit) ?? no_admin_change}</TableCell>
                                    <TableCell align="center">
                                        <TableContainer component={Paper} className={"crowd_table"}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell className={classes.tableHeadCell} align="left" style={{width: '80px'}}>Field</TableCell>
                                                        <TableCell className={classes.tableHeadCell} align="center">From</TableCell>
                                                        <TableCell className={classes.tableHeadCell} align="center">To</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {
                                                        renderChanges(row)
                                                    }
                                                </TableBody>
                                            </Table>

                                        </TableContainer>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Modal>
    );
}

export default ChangeLogModal;